import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { LayoutChangeEvent, View, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
    Easing,
    SharedValue,
    cancelAnimation,
    clamp,
    runOnJS,
    useAnimatedStyle,
    useDerivedValue,
    useFrameCallback,
    useSharedValue,
    withDecay,
    withSequence,
    withSpring,
    withTiming,
} from 'react-native-reanimated';

import { Box } from '@/components/lib/components';
import { common } from '@/styles/styles';

export type AutoScrollingListBounceProps<T> = {
    data: readonly T[];
    renderItem: (args: { item: T; index: number }) => React.ReactElement;
    keyExtractor?: (item: T, index: number) => string | number;
    speed?: number;
    spacing?: number;
    style?: ViewStyle;
    contentContainerStyle?: ViewStyle;
    frameRate?: number;
    position?: SharedValue<number>;
    autostart?: boolean;
    /**
     * Minimum bounce ratio relative to parent width.
     * When content overflows but is smaller than parentWidth * autoScrollThreshold,
     * additional virtual space is added to ensure a minimum bounce distance.
     * This prevents chaotic back-and-forth when content barely overflows.
     * @default 1.2
     */
    autoScrollThreshold?: number;
};

export interface AutoScrollingListBounceRef {
    /** Starts auto-scroll if content overflows parent width */
    start: () => void;
    /** Starts auto-scroll unconditionally */
    forceStart: () => void;
    stop: () => void;
    isActive: boolean;
}

/**
 * Applies non-linear resistance to a scroll delta when overscrolled past bounds.
 * Resistance increases quadratically as you drag further past the edge.
 */
const applyOverscrollResistance = (delta: number, overscrollAmount: number, overscrollLimit: number): number => {
    'worklet';
    const resistance = Math.max(0, 1 - overscrollAmount / overscrollLimit);
    return delta * resistance * resistance * 0.6;
};

// iOS-style spring configuration for the "snap back" effect
const SPRING_CONFIG = {
    mass: 1,
    damping: 20,
    stiffness: 150,
    overshootClamping: false,
    restDisplacementThreshold: 2,
    restSpeedThreshold: 20,
};

const AutoScrollingListBounceInner = <T,>(
    {
        data,
        renderItem,
        keyExtractor,
        speed = 1,
        spacing = 0,
        style,
        frameRate,
        position,
        autostart = true,
        autoScrollThreshold = 1.2,
        contentContainerStyle,
    }: AutoScrollingListBounceProps<T>,
    ref: React.Ref<AutoScrollingListBounceRef>
) => {
    const itemCount = data.length;

    const elements = useMemo(() => data.map((item, idx) => renderItem({ item, index: idx })), [data, renderItem]);

    const [parentWidth, setParentWidth] = useState(0);
    const [parentHeight, setParentHeight] = useState(0);
    const [contentWidth, setContentWidth] = useState(0);

    const onParentLayout = useCallback((e: LayoutChangeEvent) => {
        const { width, height } = e.nativeEvent.layout;
        setParentWidth(width);
        setParentHeight(height);
    }, []);

    const onContentLayout = useCallback((e: LayoutChangeEvent) => {
        setContentWidth(e.nativeEvent.layout.width);
    }, []);

    /**
     * Animation state:
     * - scrollPos: current scroll offset in range [0, maxScroll]
     * - direction: 1 = scrolling towards end, -1 = scrolling towards start
     * - resumeDirection: preserved direction when user releases with zero velocity
     * - maxScroll: maximum scroll offset (content overflow)
     */
    const scrollPos = useSharedValue(0);
    const direction = useSharedValue(1);
    const resumeDirection = useSharedValue(1);
    const maxScroll = useSharedValue(0);

    /**
     * Sync dimensions to shared value and handle edge cases:
     * - Clamp scrollPos when dimensions change
     * - Reset state when content no longer overflows
     * - Adjust direction if clamped exactly at bounds
     * - Apply minimum scroll distance when content barely overflows
     */
    useEffect(() => {
        const actualOverflow = Math.max(0, contentWidth - parentWidth);
        const minOverflow = parentWidth * (autoScrollThreshold - 1);

        // Apply minimum scroll distance only when there's actual overflow
        const maxScrollDistance = actualOverflow > 0 ? Math.max(actualOverflow, minOverflow) : 0;
        maxScroll.value = maxScrollDistance;

        scrollPos.value = clamp(scrollPos.value, 0, maxScrollDistance);

        if (maxScrollDistance <= 0) {
            scrollPos.value = 0;
            direction.value = 1;
        } else {
            // Small tolerance to account for floating point imprecision when checking bounds
            if (scrollPos.value <= 0.5) {
                direction.value = 1;
            } else if (scrollPos.value >= maxScrollDistance - 0.5) {
                direction.value = -1;
            }
        }
    }, [contentWidth, parentWidth, direction, maxScroll, scrollPos, autoScrollThreshold]);

    /**
     * Frame callback drives the ping-pong animation:
     * - Advances scrollPos by speed * deltaTime in current direction
     * - Reverses direction when hitting either bound
     * - Initialized as inactive, activated via useEffect when content overflows threshold
     */
    const frameInterval = frameRate ? 1000 / frameRate : null;
    const frameCb = useFrameCallback(frame => {
        if (!frame.timeSincePreviousFrame) {
            return;
        }

        if (maxScroll.value <= 0) {
            return;
        }

        const deltaTime = frameInterval ? frame.timeSincePreviousFrame / frameInterval : 1;
        let next = scrollPos.value + speed * deltaTime * direction.value;

        // Bounce at bounds
        if (next >= maxScroll.value) {
            next = maxScroll.value;
            direction.value = -1;
        } else if (next <= 0) {
            next = 0;
            direction.value = 1;
        }

        scrollPos.value = next;
    }, false);

    // Whether content overflows and can be scrolled
    const canScroll = contentWidth > parentWidth;

    // Auto-scroll whenever content overflows (minimum bounce distance is enforced via maxScroll calculation)
    const shouldAutoScroll = parentWidth > 0 && canScroll;

    // Activate/deactivate animation based on autostart and threshold
    useEffect(() => {
        if (!autostart) {
            frameCb.setActive(false);
            return;
        }
        frameCb.setActive(shouldAutoScroll);
    }, [autostart, shouldAutoScroll, frameCb]);

    // Sync scrollPos to external position SharedValue if provided
    useDerivedValue(() => {
        if (position) {
            position.value = scrollPos.value;
        }
    });

    // API interface to start/stop the animation
    const forceStart = useCallback(() => frameCb.setActive(true), [frameCb]);
    const start = useCallback(() => {
        // Only start if content overflows
        if (shouldAutoScroll) {
            frameCb.setActive(true);
        }
    }, [frameCb, shouldAutoScroll]);
    const stop = useCallback(() => frameCb.setActive(false), [frameCb]);
    useImperativeHandle(ref, () => ({ start, stop, forceStart, isActive: frameCb.isActive }));

    /**
     * Pan gesture handling with iOS-style rubber banding and momentum:
     * - onBegin: pause auto-scroll, cancel any running animations from previous gestures.
     * - onChange: update scrollPos with non-linear resistance when dragged past bounds.
     * - onFinalize:
     *   1. If past bounds: spring snap back to edge.
     *   2. If in bounds: decay (momentum) with velocity, then bounce at edge if it hits one.
     *
     * Direction logic:
     * - If velocityX !== 0: continue in the direction the user was swiping.
     * - If velocityX === 0: resume the direction from before the gesture started.
     * - After decay/spring completes at a bound: set correct ping-pong direction.
     *
     * start() internally checks shouldAutoScroll, so auto-scroll resumes
     * after user interaction if content overflows.
     */
    const pan = useMemo(
        () =>
            Gesture.Pan()
                .enabled(canScroll)
                .activeOffsetX([-10, 10])
                .failOffsetY([-5, 5])
                .onBegin(() => {
                    if (maxScroll.value <= 0) return;

                    // Stop the auto-scroll loop
                    runOnJS(stop)();
                    // Stop any ongoing animations (decay/spring) from previous gestures
                    cancelAnimation(scrollPos);

                    resumeDirection.value = direction.value;
                })
                .onChange(e => {
                    if (maxScroll.value <= 0) return;

                    let change = -e.changeX;
                    const currentPos = scrollPos.value;

                    // Max visual overscroll is half the parent width, with a fallback for unmeasured state
                    const overscrollLimit = parentWidth > 0 ? parentWidth * 0.5 : 200;

                    if (currentPos < 0) {
                        change = applyOverscrollResistance(change, Math.abs(currentPos), overscrollLimit);
                    } else if (currentPos > maxScroll.value) {
                        change = applyOverscrollResistance(change, currentPos - maxScroll.value, overscrollLimit);
                    }

                    scrollPos.value += change;
                })
                .onFinalize(e => {
                    if (maxScroll.value <= 0) return;

                    if (e.velocityX !== 0) {
                        // velocityX < 0 => swipe left => scrolling towards end
                        direction.value = e.velocityX < 0 ? 1 : -1;
                    } else {
                        direction.value = resumeDirection.value;
                    }

                    // Case 1: Overscrolled (pulling past edge) -> spring back
                    if (scrollPos.value < 0) {
                        scrollPos.value = withSpring(0, SPRING_CONFIG, finished => {
                            if (finished) {
                                direction.value = 1;
                                runOnJS(start)();
                            }
                        });
                    } else if (scrollPos.value > maxScroll.value) {
                        scrollPos.value = withSpring(maxScroll.value, SPRING_CONFIG, finished => {
                            if (finished) {
                                direction.value = -1;
                                runOnJS(start)();
                            }
                        });
                    }
                    // Case 2: In bounds -> momentum decay, then iOS-style edge bounce
                    else {
                        const flingVelocity = -e.velocityX;
                        const absoluteVelocity = Math.abs(flingVelocity);

                        // Decay with hard clamp, then manually bounce at edge
                        scrollPos.value = withDecay(
                            {
                                velocity: flingVelocity,
                                deceleration: 0.999,
                                clamp: [0, maxScroll.value],
                            },
                            finished => {
                                if (!finished) return;

                                // Bounce proportional to fling speed, capped at 50px
                                const bounceDistance = Math.min(50, absoluteVelocity * 0.02);
                                // Small tolerance to account for floating point imprecision when checking bounds
                                const isAtStart = scrollPos.value <= 0.5;
                                const isAtEnd = scrollPos.value >= maxScroll.value - 0.5;

                                // Skip bounce for very low velocities to avoid micro-jitter at the edge
                                if (isAtStart && bounceDistance > 2) {
                                    scrollPos.value = withSequence(
                                        withTiming(-bounceDistance, {
                                            duration: 120,
                                            easing: Easing.out(Easing.quad),
                                        }),
                                        withSpring(0, SPRING_CONFIG, done => {
                                            if (done) {
                                                direction.value = 1;
                                                runOnJS(start)();
                                            }
                                        })
                                    );
                                } else if (isAtEnd && bounceDistance > 2) {
                                    scrollPos.value = withSequence(
                                        withTiming(maxScroll.value + bounceDistance, {
                                            duration: 120,
                                            easing: Easing.out(Easing.quad),
                                        }),
                                        withSpring(maxScroll.value, SPRING_CONFIG, done => {
                                            if (done) {
                                                direction.value = -1;
                                                runOnJS(start)();
                                            }
                                        })
                                    );
                                } else {
                                    if (isAtStart) {
                                        direction.value = 1;
                                    } else if (isAtEnd) {
                                        direction.value = -1;
                                    }
                                    runOnJS(start)();
                                }
                            }
                        );
                    }
                }),
        [canScroll, direction, maxScroll, resumeDirection, scrollPos, start, stop, parentWidth]
    );

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: -scrollPos.value }],
    }));

    const containerStyle = useMemo<ViewStyle>(
        () => ({
            height: parentHeight || undefined,
            overflow: 'hidden',
        }),
        [parentHeight]
    );

    const contentStyle = useMemo<ViewStyle>(
        () => ({
            alignSelf: 'flex-start',
            paddingHorizontal: spacing,
        }),
        [spacing]
    );

    const itemSpacingStyle = useMemo<ViewStyle>(() => ({ marginRight: spacing }), [spacing]);

    return (
        <View style={[style, containerStyle]} onLayout={onParentLayout} pointerEvents="box-none">
            <GestureDetector gesture={pan}>
                <View style={[common.row, common.full, common.justifyStart]} pointerEvents="box-none">
                    <Animated.View
                        onLayout={onContentLayout}
                        style={[common.row, contentStyle, canScroll ? animatedStyle : undefined, contentContainerStyle]}
                        pointerEvents="box-none"
                    >
                        {elements.map((el, i) => (
                            <Box
                                key={keyExtractor ? keyExtractor(data[i], i) : i}
                                style={i < itemCount - 1 ? itemSpacingStyle : undefined}
                            >
                                {el}
                            </Box>
                        ))}
                    </Animated.View>
                </View>
            </GestureDetector>
        </View>
    );
};

/**
 * AutoScrollingListBounce is a ping-pong (bounce) scrolling list that:
 * 1. Measures content width directly via onLayout on the content row.
 * 2. Auto-scrolls from start to end, then reverses direction (ping-pong style).
 * 3. Uses a Reanimated frame callback driving a shared `scrollPos` value for continuous scroll.
 * 4. Captures pan gestures via GestureDetector with iOS-style rubber banding (resistance & spring back).
 * 5. After user interaction, continues auto-scrolling in the direction the user was scrolling.
 * 6. If content fits within parent, centers it and disables scrolling/panning.
 *
 * Auto-scroll threshold behavior:
 * - Content that fits within parent width: no scrolling or bouncing
 * - Content between 1x and `autoScrollThreshold` (default 1.2x): bounces with minimum
 * scroll distance of `parentWidth * (threshold - 1)` to ensure smooth animation
 * - Content >= threshold: bounces with natural scroll distance (contentWidth - parentWidth)
 *
 * Differences from AutoScrollingListLoop:
 * - No cloning or virtualization - simply translates the content row
 * - Bounded scroll with direction reversal instead of infinite looping
 * - Simpler implementation, lower memory footprint
 * - No initialIndex or bufferScreens props (not needed for bounded scroll)
 */
export const AutoScrollingListBounce = memo(forwardRef(AutoScrollingListBounceInner)) as <T>(
    props: AutoScrollingListBounceProps<T> & { ref?: React.Ref<AutoScrollingListBounceRef> }
) => React.ReactElement;
